import type { LessonAiDraft, LessonWorkspaceMode, TeacherLessonRecord, TeacherTourStepRecord } from "../auth/api-client";
import { normalizePythonIndentation } from "../student-ide/normalizePythonIndentation";

export const DEFAULT_MAIN_PY = `print("Hello!")

name = input("What's your name? ")
print(f"Hi, {name}!")
`;

export function mainPyFromLesson(l: TeacherLessonRecord): string {
  const raw = l.starterFilesJson;
  if (!Array.isArray(raw)) return "";
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (o.path === "main.py" && typeof o.content === "string") {
      return normalizePythonIndentation(o.content);
    }
  }
  return "";
}

export function starterFilesFromMainPy(text: string): unknown {
  return [{ path: "main.py", content: normalizePythonIndentation(text) }];
}

export function starterFilesToProjectFiles(raw: unknown): { path: string; content: string }[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [{ path: "main.py", content: normalizePythonIndentation(DEFAULT_MAIN_PY) }];
  }
  const out: { path: string; content: string }[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (typeof o.path === "string" && typeof o.content === "string") {
      out.push({ path: o.path, content: o.content });
    }
  }
  return out.length > 0 ? out : [{ path: "main.py", content: normalizePythonIndentation(DEFAULT_MAIN_PY) }];
}

export function syncMainPyInStarterFiles(
  files: readonly { path: string; content: string }[],
  mainPy: string
): { path: string; content: string }[] {
  const normalized = normalizePythonIndentation(mainPy);
  const byPath = new Map(files.map((f) => [f.path, f]));
  byPath.set("main.py", { path: "main.py", content: normalized });
  return [...byPath.values()].sort((a, b) => {
    if (a.path === "main.py") return -1;
    if (b.path === "main.py") return 1;
    return a.path.localeCompare(b.path);
  });
}

export function resolveLessonAiDraftPersistence(draft: LessonAiDraft): {
  workspaceMode: LessonWorkspaceMode;
  starterFilesJson: unknown;
} {
  const mode = draft.workspaceMode ?? "simple";
  if (mode === "advanced") {
    const files = draft.starterFiles?.length
      ? syncMainPyInStarterFiles(draft.starterFiles, draft.mainPy)
      : starterFilesToProjectFiles(starterFilesFromMainPy(draft.mainPy));
    return { workspaceMode: "advanced", starterFilesJson: files };
  }
  return { workspaceMode: "simple", starterFilesJson: starterFilesFromMainPy(draft.mainPy) };
}

export function workspaceFieldsFromLesson(
  lesson: { workspaceMode?: LessonWorkspaceMode; starterFilesJson?: unknown },
  mainPy: string
): Pick<LessonAiDraft, "workspaceMode" | "starterFiles"> {
  const workspaceMode = lesson.workspaceMode ?? "simple";
  if (workspaceMode === "advanced") {
    return {
      workspaceMode: "advanced",
      starterFiles: syncMainPyInStarterFiles(starterFilesToProjectFiles(lesson.starterFilesJson), mainPy),
    };
  }
  return { workspaceMode: "simple" };
}

export function applyPep8IndentToLessonDraft(d: LessonAiDraft): LessonAiDraft {
  return {
    ...d,
    workspaceMode: d.workspaceMode ?? "simple",
    advancedModeRationale: d.advancedModeRationale?.trim() || undefined,
    mainPy: normalizePythonIndentation(d.mainPy),
    starterFiles: d.starterFiles?.map((f) => ({
      ...f,
      content: f.path.endsWith(".py") ? normalizePythonIndentation(f.content) : f.content,
    })),
    tourSteps: d.tourSteps.map((s) => ({
      ...s,
      referenceCode:
        typeof s.referenceCode === "string" && s.referenceCode.trim()
          ? normalizePythonIndentation(s.referenceCode)
          : s.referenceCode,
    })),
    tests: d.tests.map((t) => ({
      ...t,
      testCode: normalizePythonIndentation(t.testCode),
    })),
  };
}

export function stepsToDraft(steps: TeacherTourStepRecord[]) {
  return steps.map((s) => ({
    title: s.title,
    bodyText: s.body.join("\n"),
    referenceCode: s.referenceCode ?? "",
  }));
}
